import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { chatAPI, friendsAPI } from '../services/api';
import { io, Socket } from 'socket.io-client';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProfileAvatar from './ProfileAvatar';
import './Chat.css';

interface ChatMessage {
  id: string;
  senderId: string;
  receiverId: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  sender: {
    id: string;
    firstName: string;
    lastName: string;
  };
  receiver: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface Conversation {
  friend: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    isOnline: boolean;
  };
  lastMessage: ChatMessage;
  unreadCount: number;
}

const Chat: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedFriend, setSelectedFriend] = useState<any>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [friends, setFriends] = useState<Array<{ id: string; firstName: string; lastName: string; isOnline?: boolean }>>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check if mobile view
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    if (user) {
      initializeSocket();
      loadConversations();
      friendsAPI.getFriends()
        .then(res => setFriends(res.data || []))
        .catch(() => {});
      
      // Check for friendId in URL params
      const urlParams = new URLSearchParams(location.search);
      const friendId = urlParams.get('friendId');
      
      if (friendId) {
        // Find the friend and select them
        chatAPI.getConversations().then(response => {
          const friendConversation = response.data.find((conv: Conversation) => conv.friend.id === friendId);
          if (friendConversation) {
            setSelectedFriend(friendConversation.friend);
            loadConversation(friendId);
          }
        }).catch(error => {
          console.error('Error loading specific conversation:', error);
        });
      } else {
        // Try to restore last selected friend from localStorage
        const lastSelectedFriendId = localStorage.getItem('lastSelectedFriendId');
        if (lastSelectedFriendId) {
          chatAPI.getConversations().then(response => {
            const friendConversation = response.data.find((conv: Conversation) => conv.friend.id === lastSelectedFriendId);
            if (friendConversation) {
              setSelectedFriend(friendConversation.friend);
              loadConversation(lastSelectedFriendId);
            }
          }).catch(error => {
            console.error('Error restoring conversation:', error);
          });
        }
      }
    }
  }, [user, location.search]);

  const initializeSocket = () => {
    const token = localStorage.getItem('token');
    if (token && user) {
      const newSocket = io('/', {
        auth: { token },
      });

      newSocket.on('connect', () => {
        console.log('Chat: Connected to socket');
        newSocket.emit('joinUserRoom', { userId: user.id });
      });

      newSocket.on('newMessage', (message: ChatMessage) => {
        console.log('New message received:', message);
        
        // Add message to current conversation if it's from the selected friend
        if (selectedFriend && (message.senderId === selectedFriend.id || message.receiverId === selectedFriend.id)) {
          setMessages(prev => [...prev, message]);
        }
        
        // Refresh conversations to update last message and unread count
        loadConversations();
      });

      newSocket.on('typing', (data: { userId: string, isTyping: boolean }) => {
        console.log('Typing event:', data);
        setTypingUsers(prev => {
          const newSet = new Set(prev);
          if (data.isTyping) {
            newSet.add(data.userId);
          } else {
            newSet.delete(data.userId);
          }
          return newSet;
        });
      });

      newSocket.on('userStatusChanged', (data: { userId: string, status: string }) => {
        console.log('User status changed:', data);
        // Update friend online status in conversations
        setConversations(prev => prev.map(conv => 
          conv.friend.id === data.userId 
            ? { ...conv, friend: { ...conv.friend, isOnline: data.status === 'online' } }
            : conv
        ));
        
                 // Update selected friend status
         if (selectedFriend && selectedFriend.id === data.userId) {
           setSelectedFriend((prev: any) => ({ ...prev, isOnline: data.status === 'online' }));
         }
      });

      setSocket(newSocket);

      return () => {
        if (typingTimeout) {
          clearTimeout(typingTimeout);
        }
        newSocket.close();
      };
    }
  };

  const loadConversations = async () => {
    try {
      const response = await chatAPI.getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setError(t('chat.failedConversations'));
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (friendId: string) => {
    try {
      const response = await chatAPI.getConversation(friendId);
      setMessages(response.data);
      
      // Mark messages as read
      await chatAPI.markAsRead(friendId);
      await loadConversations(); // Refresh to update unread counts
    } catch (error) {
      console.error('Error loading conversation:', error);
      setError(t('chat.failedConversation'));
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedFriend || !socket) return;

    try {
      const message = await chatAPI.sendMessage(selectedFriend.id, newMessage.trim());
      setMessages(prev => [...prev, message.data]);
      setNewMessage('');
      
      // Refresh conversations to update last message
      loadConversations();
      
      // Stop typing indicator
      if (socket && user) {
        socket.emit('typing', { receiverId: selectedFriend.id, isTyping: false });
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    
    if (!selectedFriend || !socket || !user) return;
    
    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    // Send typing start
    socket.emit('typing', { userId: user.id, receiverId: selectedFriend.id, isTyping: true });
    
    // Set timeout to stop typing indicator
    const timeout = setTimeout(() => {
      socket.emit('typing', { userId: user.id, receiverId: selectedFriend.id, isTyping: false });
    }, 2000);
    
    setTypingTimeout(timeout);
  };

  const handleSelectFriend = (friend: any) => {
    setSelectedFriend(friend);
    loadConversation(friend.id);
    
    // Save selected friend to localStorage for persistence
    localStorage.setItem('lastSelectedFriendId', friend.id);
    
    // Update URL to include friendId
    window.history.replaceState(null, '', `/chat?friendId=${friend.id}`);
    
    // Mark messages as read when conversation is opened
    if (friend.unreadCount > 0) {
      chatAPI.markAsRead(friend.id);
      // Refresh conversations to update unread counts
      chatAPI.getConversations().then(response => {
        setConversations(response.data);
      }).catch(error => {
        console.error('Error refreshing conversations:', error);
      });
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const clearSelectedFriend = () => {
    setSelectedFriend(null);
    setMessages([]);
    localStorage.removeItem('lastSelectedFriendId');
    window.history.replaceState(null, '', '/chat');
  };

  const startConversationWith = (friend: { id: string; firstName: string; lastName: string; isOnline?: boolean }) => {
    setShowNewChat(false);
    handleSelectFriend(friend);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (loading) {
    return (
      <div className="page-with-sidebar">
        <div style={{ 
          textAlign: 'center', 
          padding: '60px',
          color: 'rgba(255,255,255,0.6)'
        }}>
          <div className="spinner-border text-light" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: '20px' }}>{t('chat.loadingChat')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-modern chat-page">
      {/* Chat Header */}
      <div className="dashboard-header chat-hide-mobile">
        <div>
          <h1>💬 {t('chat.title')}</h1>
          <p>{t('chat.subtitle')}</p>
        </div>
      </div>

      {/* Chat Stats */}
      <div className="stats-grid chat-hide-mobile">
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--accent)' }}>{conversations.length}</div>
          <div className="stat-label">{t('chat.conversationsStat')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#FF9800' }}>{conversations.reduce((sum, conv) => sum + conv.unreadCount, 0)}</div>
          <div className="stat-label">{t('chat.unread')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#2196F3' }}>{conversations.filter(conv => conv.friend.isOnline).length}</div>
          <div className="stat-label">{t('chat.onlineFriends')}</div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'rgba(244, 67, 54, 0.1)',
          border: '1px solid #f44336',
          color: '#f44336',
          padding: '15px 20px',
          borderRadius: '12px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Chat Interface */}
      <div className="game-section chat-section">
        <div className="chat-shell" style={{ 
          display: 'flex',
          height: '70vh',
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          overflow: 'hidden'
        }}>
          
          {/* Conversations Sidebar */}
          {(!isMobile || !selectedFriend) && (
          <div className="chat-conversations" style={{
            width: isMobile ? '100%' : '350px',
            minWidth: isMobile ? 'auto' : '350px',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'rgba(255, 255, 255, 0.02)',
            overflow: 'hidden',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ padding: '20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <h3 style={{ color: 'white', margin: 0, fontSize: '18px' }}>{t('chat.conversations')}</h3>
              <button
                type="button"
                className="chat-new-btn"
                onClick={() => setShowNewChat(v => !v)}
              >
                + {t('chat.newChat')}
              </button>
            </div>

            {showNewChat && (
              <div className="chat-new-picker">
                {friends.length === 0 ? (
                  <p className="chat-new-empty">{t('chat.noFriendsForChat')}</p>
                ) : (
                  friends.map(friend => (
                    <button
                      key={friend.id}
                      type="button"
                      className="chat-new-friend"
                      onClick={() => startConversationWith(friend)}
                    >
                      {friend.firstName} {friend.lastName}
                    </button>
                  ))
                )}
              </div>
            )}
            
            <div style={{ height: 'calc(100% - 80px)', overflow: 'auto' }}>
              {conversations.length === 0 ? (
                <div style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: 'rgba(255,255,255,0.6)'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}>💬</div>
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    {t('chat.noConversationsLine1')}<br />
                    {t('chat.noConversationsLine2')}
                  </p>
                </div>
              ) : (
                conversations.map(conversation => (
                  <div
                    key={conversation.friend.id}
                    onClick={() => handleSelectFriend(conversation.friend)}
                    style={{
                      padding: '15px 20px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                      cursor: 'pointer',
                      background: selectedFriend?.id === conversation.friend.id 
                        ? 'rgba(76, 175, 80, 0.1)' 
                        : 'transparent',
                      borderLeft: selectedFriend?.id === conversation.friend.id 
                        ? '3px solid var(--accent)' 
                        : '3px solid transparent',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedFriend?.id !== conversation.friend.id) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedFriend?.id !== conversation.friend.id) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ position: 'relative' }}>
                        <div className="user-avatar" style={{ 
                          width: '45px', 
                          height: '45px',
                          fontSize: '16px'
                        }}>
                          {conversation.friend.firstName[0].toUpperCase()}
                        </div>
                        <div style={{
                          position: 'absolute',
                          bottom: '-2px',
                          right: '-2px',
                          width: '14px',
                          height: '14px',
                          borderRadius: '50%',
                          background: conversation.friend.isOnline ? 'var(--accent)' : '#f44336',
                          border: '2px solid #1a1a1a'
                        }} />
                      </div>
                      
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '5px'
                        }}>
                          <h4 style={{ 
                            color: 'white', 
                            margin: 0, 
                            fontSize: '14px',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {conversation.friend.firstName} {conversation.friend.lastName}
                          </h4>
                          {conversation.unreadCount > 0 && (
                            <span style={{
                              background: 'var(--accent)',
                              color: 'white',
                              borderRadius: '10px',
                              padding: '2px 6px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              minWidth: '18px',
                              textAlign: 'center'
                            }}>
                              {conversation.unreadCount}
                            </span>
                          )}
                        </div>
                        
                        {conversation.lastMessage && (
                          <p style={{
                            color: 'rgba(255,255,255,0.6)',
                            margin: 0,
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {conversation.lastMessage.senderId === user?.id ? t('chat.you') : ''}
                            {conversation.lastMessage.message}
                          </p>
                        )}
                        
                        {typingUsers.has(conversation.friend.id) && (
                          <p style={{
                            color: 'var(--accent)',
                            margin: 0,
                            fontSize: '12px',
                            fontStyle: 'italic'
                          }}>
                            {t('chat.typing')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          )}

          {/* Messages Area */}
          {(!isMobile || selectedFriend) && (
          <div className="chat-messages" style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: 'rgba(255, 255, 255, 0.01)'
          }}>
            {selectedFriend ? (
              <>
                {/* Chat Header */}
                <div style={{
                  padding: '20px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(255, 255, 255, 0.03)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {isMobile && (
                      <button
                        onClick={clearSelectedFriend}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          fontSize: '18px',
                          cursor: 'pointer',
                          padding: '5px'
                        }}
                      >
                        ←
                      </button>
                    )}
                    <div style={{ position: 'relative' }}>
                      <div className="user-avatar" style={{ 
                        width: '50px', 
                        height: '50px',
                        fontSize: '20px'
                      }}>
                        {selectedFriend.firstName[0].toUpperCase()}
                      </div>
                      <div style={{
                        position: 'absolute',
                        bottom: '-2px',
                        right: '-2px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: selectedFriend.isOnline ? 'var(--accent)' : '#f44336',
                        border: '2px solid #1a1a1a'
                      }} />
                    </div>
                    <div>
                      <h3 style={{ color: 'white', margin: 0, fontSize: '18px' }}>
                        {selectedFriend.firstName} {selectedFriend.lastName}
                      </h3>
                      <p style={{ 
                        color: selectedFriend.isOnline ? 'var(--accent)' : 'rgba(255,255,255,0.6)', 
                        margin: 0, 
                        fontSize: '12px' 
                      }}>
                        {selectedFriend.isOnline ? t('chat.online') : t('chat.offline')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div style={{
                  flex: 1,
                  overflow: 'auto',
                  padding: '20px'
                }}>
                  {messages.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '60px 20px',
                      color: 'rgba(255,255,255,0.6)'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}>💬</div>
                      <p style={{ margin: 0, fontSize: '16px' }}>
                        {t('chat.noMessagesYet')}
                      </p>
                    </div>
                  ) : (
                    messages.map((message, index) => {
                      const isOwn = message.senderId === user?.id;
                      const showDate = index === 0 || 
                        new Date(messages[index - 1].createdAt).toDateString() !== new Date(message.createdAt).toDateString();
                      
                      return (
                        <div key={message.id}>
                          {showDate && (
                            <div style={{
                              textAlign: 'center',
                              margin: '20px 0',
                              color: 'rgba(255,255,255,0.4)',
                              fontSize: '12px'
                            }}>
                              {new Date(message.createdAt).toLocaleDateString()}
                            </div>
                          )}
                          
                          <div style={{
                            display: 'flex',
                            justifyContent: isOwn ? 'flex-end' : 'flex-start',
                            marginBottom: '15px'
                          }}>
                            <div style={{
                              maxWidth: '70%',
                              padding: '12px 16px',
                              borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                              background: isOwn 
                                ? 'linear-gradient(135deg, var(--accent) 0%, var(--accent-press) 100%)'
                                : 'rgba(255,255,255,0.1)',
                              color: 'white',
                              fontSize: '14px',
                              lineHeight: '1.4',
                              wordBreak: 'break-word'
                            }}>
                              <p style={{ margin: 0 }}>{message.message}</p>
                              <p style={{
                                margin: '5px 0 0 0',
                                fontSize: '11px',
                                opacity: 0.7,
                                textAlign: isOwn ? 'right' : 'left'
                              }}>
                                {new Date(message.createdAt).toLocaleTimeString([], { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} style={{
                  padding: '20px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(255, 255, 255, 0.03)'
                }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                    <input
                      type="text"
                      value={newMessage}
                      onChange={handleTyping}
                      placeholder={t('chat.typeMessage')}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '25px',
                        color: 'white',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="btn-join"
                      style={{
                        padding: '12px 20px',
                        borderRadius: '25px',
                        opacity: newMessage.trim() ? 1 : 0.5
                      }}
                    >
                      {t('chat.send')}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
                color: 'rgba(255,255,255,0.6)'
              }}>
                <div>
                  <div style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.3 }}>💬</div>
                  <h3 style={{ color: 'white', marginBottom: '10px' }}>{t('chat.selectConversationTitle')}</h3>
                  <p style={{ margin: 0, fontSize: '16px' }}>
                    {t('chat.selectConversationDesc')}
                  </p>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat; 